import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { funeralStoreService } from "../../services/funeralStore";
import {
    Search,
    Package,
    Store,
    ShoppingCart,
    Star,
    Loader2,
    Check,
    ChevronsUpDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getImageUrl } from "../../utils/imageUtils";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/hooks/useDebounce";

export default function ResellerFuneralStore({ store }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [products, setProducts] = useState([]);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [selectedCategory, setSelectedCategory] = useState(() => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get("collection_id") || "all";
    });
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Pagination State
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    // Handle category change (sync with URL)
    const handleCategoryChange = (colId) => {
        if (colId === selectedCategory) return;
        setSelectedCategory(colId);
        const searchParams = new URLSearchParams(location.search);
        if (colId === "all") {
            searchParams.delete("collection_id");
        } else {
            searchParams.set("collection_id", colId);
        }
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    // Sync state with URL (bidirectional)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const colId = searchParams.get("collection_id") || "all";
        if (colId !== selectedCategory) {
            setSelectedCategory(colId);
        }
    }, [location.search]);

    useEffect(() => {
        fetchCollections();
    }, []);

    useEffect(() => {
        // Reset when filters change
        setPage(1);
        setProducts([]);
        setHasMore(true);
        fetchProducts(1, true);
    }, [selectedCategory, debouncedSearch]);

    const fetchCollections = async () => {
        try {
            const colData = await funeralStoreService.reseller.getCollections();
            setCollections(colData);
        } catch (error) {
            console.error("Failed to load collections:", error);
        }
    };

    const fetchProducts = async (pageNum, isInitial = false) => {
        try {
            if (isInitial) setLoading(true);
            else setLoadingMore(true);

            const params = {
                page: pageNum,
                limit: 12,
                keyword: debouncedSearch
            };
            if (selectedCategory !== "all") params.collection_id = selectedCategory;

            const response = await funeralStoreService.reseller.getProducts(params);

            // Handle both array (legacy) and object with data property (new pagination)
            const newItems = Array.isArray(response) ? response : (response.data || []);
            const totalPages = response.totalPages || 1;

            setProducts(prev => isInitial ? newItems : [...prev, ...newItems]);
            setHasMore(pageNum < totalPages && newItems.length > 0);
        } catch (error) {
            toast.error("Failed to load products");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const lastProductElementRef = useCallback(node => {
        if (loading || loadingMore) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => {
                    const nextPage = prevPage + 1;
                    fetchProducts(nextPage);
                    return nextPage;
                });
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, loadingMore, hasMore]);

    const handleBuyNow = (product) => {
        navigate(`/reseller/funeral-store/product/${product.product_id}`);
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading catalog...</div>;

    return (
        <div className="space-y-8 animate-fade-in translate-y-4">
            <div className="bg-white p-8 rounded-3xl border border-indigo-100/50 shadow-sm shadow-indigo-100/20">
                <div className="space-y-2">
                    <div className="flex items-center gap-3 text-indigo-600 mb-1">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                            <Store className="w-5 h-5" />
                        </div>
                        <span className="text-sm font-bold uppercase tracking-wider">Infinity Funeral Supply</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Funeral Supplies</h2>
                    <p className="text-slate-500 max-w-md">Exclusive catalog for professional funeral products. Buy directly at base prices with no hidden commissions.</p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                    />
                </div>
                <div className="w-full md:w-64">
                    <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={dropdownOpen}
                                className="w-full justify-between h-12 bg-white rounded-xl border-slate-200 hover:bg-slate-50"
                            >
                                {selectedCategory === "all"
                                    ? "All Products"
                                    : collections.find((col) => col.collection_id === selectedCategory)?.name || "Filter by Collection"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0" align="end">
                            <Command>
                                <CommandInput placeholder="Search collections..." />
                                <CommandList>
                                    <CommandEmpty>No collections found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            key="all"
                                            value="All Products"
                                            onSelect={() => {
                                                handleCategoryChange("all");
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    selectedCategory === "all" ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            All Products
                                        </CommandItem>
                                        {collections
                                            .filter(col => col.is_active !== false && col.product_count > 0)
                                            .map((col) => (
                                            <CommandItem
                                                key={col.collection_id}
                                                value={col.name}
                                                onSelect={() => {
                                                    handleCategoryChange(col.collection_id);
                                                    setDropdownOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        selectedCategory === col.collection_id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {col.name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.length === 0 && !loading ? (
                    <div className="col-span-full py-24 text-center text-slate-400 bg-white rounded-3xl border border-dashed">
                        <Package className="w-16 h-16 mx-auto mb-4 opacity-10" />
                        <p className="text-lg font-medium text-slate-300">No products found in this section.</p>
                    </div>
                ) : (
                    products.map((product, index) => (
                        <div
                            key={product.product_id}
                            ref={index === products.length - 1 ? lastProductElementRef : null}
                            className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-indigo-500/5 transition-all duration-500 flex flex-col"
                        >
                            <div className="aspect-square relative overflow-hidden bg-slate-50/50 p-6 cursor-pointer" onClick={() => handleBuyNow(product)}>
                                <img
                                    src={getImageUrl(product.images?.[0]?.url || product.image_url)}
                                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700"
                                    alt={product.name}
                                />
                                {product.stock <= 5 && product.stock > 0 && (
                                    <Badge className="absolute top-4 left-4 bg-amber-500 text-[10px] font-bold uppercase tracking-tight">Low Stock</Badge>
                                )}
                                {product.stock === 0 && (
                                    <Badge variant="destructive" className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-tight">Out of Stock</Badge>
                                )}
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-400">
                                        <ShoppingCart className="w-4 h-4" />
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 flex-1 flex flex-col">
                                <h3
                                    className="font-bold text-slate-900 line-clamp-1 mb-1 group-hover:text-indigo-600 transition-colors cursor-pointer"
                                    onClick={() => handleBuyNow(product)}
                                >
                                    {product.name}
                                </h3>
                                <p className="text-[10px] text-slate-400 font-mono uppercase tracking-widest mb-3">{product.sku}</p>

                                {/* Star Ratings - Hidden as per request */}
                                {/* 
                                <div className="flex items-center gap-0.5 mb-4">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <Star key={star} className="w-3 h-3 fill-amber-400 text-amber-400" />
                                    ))}
                                    <span className="text-[10px] text-slate-400 ml-1.5 font-medium">(24 reviews)</span>
                                </div>
                                */}

                                <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-50">
                                    <div className="flex flex-col">
                                        <span className="text-xl font-black text-slate-900">
                                            ${(product.overridden_price || product.base_price || 0).toFixed(2)}
                                        </span>
                                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">Reseller Rate</span>
                                    </div>
                                    <Button
                                        size="icon"
                                        onClick={() => handleBuyNow(product)}
                                        disabled={product.stock === 0}
                                        className="h-10 w-10 rounded-full bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all hover:scale-110 active:scale-95"
                                    >
                                        <ShoppingCart className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
                {(loading || loadingMore) && (
                    <div className="col-span-full py-12 flex flex-col items-center justify-center gap-3 text-indigo-500">
                        <Loader2 className="w-8 h-8 animate-spin" />
                        <p className="text-xs font-bold uppercase tracking-widest opacity-50">Loading Catalog...</p>
                    </div>
                )}
            </div>
        </div>
    );
}
